import {
    type GoldRushResponse,
    type Nullable,
    type Pagination,
    type PaginationLinks,
} from "../types/Generic.types";
import { type Execution } from "./execution";

export async function* paginateEndpoint<
    T extends
        | Nullable<{ pagination: Pagination }>
        | Nullable<{ links: PaginationLinks }>
        | null,
>(
    endpoint: URL,
    execution: Execution,
    parseData: (data: GoldRushResponse<T>) => GoldRushResponse<T>,
    implementation: "pagination" | "links"
): AsyncIterable<GoldRushResponse<T>> {
    let _endpoint: URL = new URL(endpoint);
    let hasMore: boolean = true;
    let page_number: number = +(_endpoint.searchParams.get("page-number") ?? 0);

    while (hasMore) {
        try {
            if (implementation === "pagination") {
                _endpoint.searchParams.set(
                    "page-number",
                    page_number.toString()
                );

                const parsedData = await execution.execute<T>(
                    _endpoint,
                    parseData
                );

                if (parsedData.error) {
                    throw parsedData;
                }

                if (
                    !(
                        parsedData.data as {
                            pagination: Pagination;
                        }
                    )?.pagination?.has_more
                ) {
                    hasMore = false;
                } else {
                    page_number++;
                }

                yield parsedData as GoldRushResponse<T>;
            } else if (implementation === "links") {
                const parsedData = await execution.execute<T>(
                    _endpoint,
                    parseData
                );
                const prevLink: string | null =
                    (
                        parsedData.data as {
                            links: PaginationLinks;
                        }
                    )?.links?.prev || null;

                if (!prevLink) {
                    hasMore = false;
                } else {
                    _endpoint = new URL(
                        `${prevLink}?${_endpoint.searchParams}`
                    );
                }

                yield parsedData as GoldRushResponse<T>;
            }
        } catch (error: unknown) {
            hasMore = false;
            const err = error as {
                error_code?: number;
                cause?: { code?: number; message?: string };
                error_message?: string;
            };
            yield {
                data: null,
                error: true,
                error_code: err?.cause?.code || err?.error_code || 500,
                error_message:
                    err?.cause?.message ||
                    err?.error_message ||
                    "Internal server error",
            };
        }
    }
}
